import { StoredEnvironment } from '@amzn/awscat-portfolio-assessment-types';
import { Button, ExpandableSection, Header } from '@amzn/awsui-components-react';
import { useState } from 'react';

import { constants } from '../../../common/Localization';
import { format } from '../../../common/util';
import AWSUIFormBuilder, { MetadataItem } from '../../common/AWSUIFormBuilder';
import ConfirmationModal from '../../common/ConfirmationModal';
import { DevValidationConstants, ProdValidationConstants, ValidationConstants } from './constants';

type Props = {
    environment: StoredEnvironment;
    environmentIndex: number;
    setEnvironment: (index: number, env: StoredEnvironment) => void;
    setEnvValidity: (index: number, isValid: boolean) => void;
    deleteEnvironment: (index: number) => void;
}
const MainframeEnvironment = ({ environment, environmentIndex, setEnvironment, setEnvValidity, deleteEnvironment }: Props): JSX.Element => {
    const [ deleteFrictionVisible, setDeleteFrictionVisible ] = useState(false);
    const isProd = environment.envType === 'prod';
    const envTypes = isProd ? [{
        value: 'prod',
        text: constants.PROD,
    }] : [{
        value: 'test',
        text: constants.TEST,
        uiDisabled: true,
    }, {
        value: 'dev',
        text: constants.DEV,
    }];


    //compute actual bounds from env
    const mipsMin = isProd ? ProdValidationConstants.mipsMin : DevValidationConstants.mipsMin;
    const diskUsedTBMin = isProd ? ProdValidationConstants.diskUsedTBMin : DevValidationConstants.diskUsedTBMin;
    const backupTapeTBMin = isProd ? ProdValidationConstants.backupTapeTBMin : DevValidationConstants.backupTapeTBMin;
    const backupTapeRetrTBMin = isProd ? ProdValidationConstants.backupTapeRetrTBMin : DevValidationConstants.backupTapeRetrTBMin;
    const hsmAndSmsTapeTBMin = isProd ? ProdValidationConstants.hsmAndSmsTapeTBMin : DevValidationConstants.hsmAndSmsTapeTBMin;
    const hsmAndSmsTapeRetrTBMin = isProd ? ProdValidationConstants.hsmAndSmsTapeRetrTBMin : DevValidationConstants.hsmAndSmsTapeRetrTBMin;

    const formMetadata: MetadataItem<StoredEnvironment>[] = [
        {
            identifier: 'name',
            type: 'string',
            label: constants.ENVIRONMENT_NAME + ' *',
            validation: {
                minLen: ValidationConstants.nameLenMin,
                maxLen: ValidationConstants.nameLenMax,
            },
            required: true,
        },
        {
            identifier: 'envType',
            type: 'enum',
            label: constants.ENVIRONMENT_TYPE + ' *',
            enum: envTypes,
            required: true,
        },
        {
            identifier: 'oltpHrs',
            type: 'number',
            validation: {
                min: ValidationConstants.oltpHrsMin,
                max: ValidationConstants.oltpHrsMax,
            },
            label: constants.OLTP_UPTIME_PER_WEEK + ' *',
            required: true,
        },
        {
            identifier: 'batchHrs',
            type: 'number',
            validation: {
                min: ValidationConstants.batchHrsMin,
                max: ValidationConstants.batchHrsMax,
            },
            label: constants.BATCH_UPTIME_PER_WEEK + ' *',
            required: true,
        },
        {
            identifier: 'avgOltpMips',
            type: 'number',
            validation: {
                min: mipsMin,
                max: ValidationConstants.mipsMax,
            },
            label: `${constants.AVG_MIPS_USAGE_OLTP} ${
                isProd ? '' : format(constants.BY_DEFAULT_PERCENT_OF_PROD_VALUE, ['10'])} *`,
            required: true,
        },
        {
            identifier: 'avgBatchMips',
            type: 'number',
            validation: {
                min: mipsMin,
                max: ValidationConstants.mipsMax,
            },
            label: `${constants.AVG_MIPS_USAGE_BATCH} ${
                isProd ? '' : format(constants.BY_DEFAULT_PERCENT_OF_PROD_VALUE, ['10'])} *`,
            required: true,
        },
        {
            identifier: 'avgDBMips',
            type: 'number',
            validation: {
                min: mipsMin,
                max: ValidationConstants.mipsMax,
            },
            label: `${constants.AVG_MIPS_USAGE_DB} ${
                isProd ? '' : format(constants.BY_DEFAULT_PERCENT_OF_PROD_VALUE, ['10'])} *`,
            required: true,
        },
        {
            identifier: 'avgTotalMips',
            type: 'number',
            label: constants.AVG_MIPS_USAGE_TOTAL,
            uiDisabled: true,
        },
        {
            identifier: 'dbDiskUsedTB',
            type: 'number',
            validation: {
                min: diskUsedTBMin,
                max: ValidationConstants.diskUsedTBMax,
            },
            label: `${constants.DISK_STORAGE_USED} ${
                isProd ? '' : format(constants.BY_DEFAULT_PERCENT_OF_PROD_VALUE, ['5'])} *`,
            required: true,
        },
        {
            identifier: 'filesDiskUsedTB',
            type: 'number',
            validation: {
                min: diskUsedTBMin,
                max: ValidationConstants.diskUsedTBMax,
            },
            label: `${constants.DISK_STORAGE_USED_FILE} ${
                isProd ? '' : format(constants.BY_DEFAULT_PERCENT_OF_PROD_VALUE, ['5'])} *`,
            required: true,
        },
        {
            identifier: 'backupTapeTB',
            type: 'number',
            validation: {
                min: backupTapeTBMin,
                max: ValidationConstants.diskUsedTBMax,
            },
            label: constants.BACKUP_TAPE_STORAGE_USED,
        },
        {
            identifier: 'backupTapeRetrTB',
            type: 'number',
            validation: {
                min: backupTapeRetrTBMin,
                max: ValidationConstants.diskUsedTBMax,
            },
            label: constants.BACKUP_TAPE_STORAGE_RETRIEVAL,
        },
        {
            identifier: 'hsmAndSmsTapeTB',
            type: 'number',
            validation: {
                min: hsmAndSmsTapeTBMin,
                max: ValidationConstants.diskUsedTBMax,
            },
            label: constants.HSM_SMS_TAPE_STORAGE_USED,
        },
        {
            identifier: 'hsmAndSmsTapeRetrTB',
            type: 'number',
            validation: {
                min: hsmAndSmsTapeRetrTBMin,
                max: ValidationConstants.diskUsedTBMax,
            },
            label: constants.HSM_SMS_TAPE_STORAGE_RETRIEVAL,
        },
        {
            identifier: 'appEnvCount',
            type: 'number',
            validation: {
                min: ValidationConstants.envCountMin,
                max: ValidationConstants.envCountMax,
            },
            label: constants.SEPARATE_APP_ENVS,
        },
        {
            identifier: 'dbEnvCount',
            type: 'number',
            validation: {
                min: ValidationConstants.envCountMin,
                max: ValidationConstants.envCountMax,
            },
            label: constants.SEPARATE_DB_ENVS,
        },
        {
            identifier: 'dataTransferOutGB',
            type: 'number',
            validation: {
                min: ValidationConstants.dataTransferOutGBMin,
                max: ValidationConstants.dataTransferOutGBMax,
            },
            label: `${constants.DATA_TRANSFER_OUT} ${
                isProd ? '' : format(constants.BY_DEFAULT_PERCENT_OF_PROD_VALUE, ['0.1'])} *`,
        }
    ];
    const onDeleteEnv = () => {
        deleteEnvironment(environmentIndex);
        setDeleteFrictionVisible(false);
    };
    const onUpdateEnv = (updatedEnvironment: StoredEnvironment, isValid?: boolean) => {
        setEnvironment(environmentIndex, updatedEnvironment);
        setEnvValidity(environmentIndex, isValid || false);
    };
    return (
        <ExpandableSection variant='container' data-testid='MainframeForm' header={
            <Header actions={
                environment.envType === 'prod' ? null: <Button onClick={() => { setDeleteFrictionVisible(true); }}>{constants.DELETE_BUTTON_TEXT}</Button>}>
                {`${constants.ENVIRONMENT} ${environmentIndex + 1}`}
            </Header>}>
            <AWSUIFormBuilder
                item={environment}
                metadata={formMetadata}
                onUpdate={onUpdateEnv}
                primaryAction={constants.CREATE_BUTTON_TEXT}/>
            { deleteFrictionVisible ? (
                <ConfirmationModal
                    confirmButton={constants.DELETE_BUTTON_TEXT}
                    onConfirm={onDeleteEnv}
                    onDismiss={() => { setDeleteFrictionVisible(false); }}
                    header={constants.CONFIRM_DELETE}
                    visible
                >
                    {constants.DELETE_MAINFRAME_ENV_CONFIRM_MSG}
                </ConfirmationModal>
            ) : null }
        </ExpandableSection>
    );
};

export default MainframeEnvironment;
